import { useEffect, useState } from "react";
import { fetchDomains, fetchClients, addDomain, deleteDomain, triggerDomainScan, ApiError } from "../api/client.js";
import type { DomainView, ClientView } from "@atlas/shared/types";

// ─── Webhook notification helpers ─────────────────────────────────────────────

const CHANNELS_KEY = "atlas_notification_channels";

interface NotificationChannel {
  id: string;
  type: "slack" | "discord" | "teams";
  name: string;
  webhookUrl: string;
  enabled: boolean;
}

function loadChannels(): NotificationChannel[] {
  try {
    const raw = localStorage.getItem(CHANNELS_KEY);
    return raw ? (JSON.parse(raw) as NotificationChannel[]) : [];
  } catch {
    return [];
  }
}

function buildPayload(type: NotificationChannel["type"], domain: DomainView, clientName: string): unknown {
  const snap = domain.latestSnapshot;
  const expiry = snap ? `${new Date(snap.notAfter).toLocaleDateString()} (${snap.daysRemaining}d remaining)` : "Not scanned";
  const risk = snap?.risk ?? "unknown";
  const riskEmoji = risk === "red" ? "🔴" : risk === "amber" ? "🟡" : risk === "green" ? "🟢" : "⚪";
  const body = [
    `${riskEmoji} *Manual alert triggered for* \`${domain.hostname}\``,
    `• Client: ${clientName}`,
    `• Expires: ${expiry}`,
    `• Risk: ${risk}`,
    `• OCSP: ${snap?.ocspStatus ?? "—"}`,
    `• Compliance: ${snap?.complianceStatus ?? "—"}`,
  ].join("\n");

  if (type === "slack") {
    return { text: body.replace(/\*/g, "*") };
  }
  if (type === "discord") {
    return { content: body.replace(/`/g, "`").replace(/\*/g, "**") };
  }
  // teams — MessageCard
  const color = risk === "red" ? "FF4444" : risk === "amber" ? "FFAA00" : "44BB44";
  return {
    "@type": "MessageCard",
    "@context": "http://schema.org/extensions",
    themeColor: color,
    summary: `ATLAS alert: ${domain.hostname}`,
    title: `${riskEmoji} ATLAS — ${domain.hostname}`,
    text: `**Client:** ${clientName} &nbsp;|&nbsp; **Expires:** ${expiry} &nbsp;|&nbsp; **Risk:** ${risk} &nbsp;|&nbsp; **OCSP:** ${snap?.ocspStatus ?? "—"}`,
  };
}

async function sendToChannels(domain: DomainView, clientName: string): Promise<{ sent: number; failed: number }> {
  const channels = loadChannels().filter((c) => c.enabled);
  let sent = 0;
  let failed = 0;
  await Promise.all(
    channels.map(async (ch) => {
      try {
        const res = await fetch(ch.webhookUrl, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(buildPayload(ch.type, domain, clientName)),
        });
        if (res.ok) sent++;
        else failed++;
      } catch {
        failed++;
      }
    })
  );
  return { sent, failed };
}

interface FormState {
  clientId: string;
  hostname: string;
  port: string;
  tags: string;
  isInternal: boolean;
}

const EMPTY_FORM: FormState = { clientId: "", hostname: "", port: "443", tags: "", isInternal: false };

function riskBadge(risk: string) {
  const map: Record<string, string> = {
    green:   "bg-green-900 text-green-300",
    amber:   "bg-amber-900 text-amber-300",
    red:     "bg-red-900 text-red-300",
    unknown: "bg-gray-800 text-gray-400",
  };
  return map[risk] ?? map["unknown"]!;
}

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

function complianceBadge(status: string) {
  const map: Record<string, string> = {
    compliant:     "text-green-400",
    non_compliant: "text-red-400",
    legacy:        "text-amber-400",
  };
  return map[status] ?? "text-gray-500";
}

export default function DomainsPage() {
  const [domains, setDomains]   = useState<DomainView[]>([]);
  const [clients, setClients]   = useState<ClientView[]>([]);
  const [error, setError]       = useState<string | null>(null);
  const [loading, setLoading]   = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm]         = useState<FormState>(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError]   = useState<string | null>(null);
  const [scanning, setScanning]     = useState<number | null>(null);
  const [scanMsg, setScanMsg]       = useState<Record<number, string>>({});
  const [deleting, setDeleting]     = useState<number | null>(null);
  const [notifying, setNotifying]   = useState<number | null>(null);
  const [notifyMsg, setNotifyMsg]   = useState<Record<number, string>>({});

  // Filter state
  const [filterClient, setFilterClient] = useState<string>("all");
  const [filterRisk, setFilterRisk]     = useState<string>("all");
  const [filterType, setFilterType]     = useState<string>("all");

  function load() {
    setLoading(true);
    Promise.all([fetchDomains(), fetchClients()])
      .then(([d, c]) => { setDomains(d); setClients(c); })
      .catch((e: unknown) => setError(e instanceof ApiError ? e.message : "Failed to load"))
      .finally(() => setLoading(false));
  }

  useEffect(load, []);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setFormError(null);
    setSubmitting(true);

    const tags = form.tags
      .split(",")
      .map((t) => t.trim())
      .filter(Boolean);

    try {
      const { id } = await addDomain({
        clientId: parseInt(form.clientId, 10),
        hostname: form.hostname.trim(),
        port: parseInt(form.port, 10) || 443,
        tags,
        isInternal: form.isInternal,
      });
      // Trigger an immediate scan after adding (skip for internal domains)
      if (!form.isInternal) {
        await triggerDomainScan(id).catch(() => null);
      }
      setForm(EMPTY_FORM);
      setShowForm(false);
      load();
    } catch (e) {
      setFormError(e instanceof ApiError ? e.message : "Failed to add domain");
    } finally {
      setSubmitting(false);
    }
  }

  async function handleScan(domainId: number, hostname: string) {
    setScanning(domainId);
    setScanMsg((m) => ({ ...m, [domainId]: "" }));
    try {
      const result = await triggerDomainScan(domainId) as { skipped?: boolean; daysRemaining?: number; agentQueued?: boolean };
      setScanMsg((m) => ({
        ...m,
        [domainId]: result.agentQueued
          ? "Queued — refresh in a moment"
          : result.skipped
          ? "Cert unchanged"
          : `Scanned — ${result.daysRemaining ?? "?"}d remaining`,
      }));
      load();
    } catch (e) {
      setScanMsg((m) => ({
        ...m,
        [domainId]: e instanceof ApiError ? e.message : "Scan failed",
      }));
    } finally {
      setScanning(null);
    }
  }

  async function handleDelete(domainId: number, hostname: string) {
    if (!window.confirm(`Remove ${hostname} from monitoring?`)) return;
    setDeleting(domainId);
    try {
      await deleteDomain(domainId);
      load();
    } catch (e) {
      alert(e instanceof ApiError ? e.message : "Failed to delete domain");
    } finally {
      setDeleting(null);
    }
  }

  async function handleNotify(domain: DomainView) {
    const channels = loadChannels().filter((c) => c.enabled);
    if (channels.length === 0) {
      setNotifyMsg((m) => ({ ...m, [domain.id]: "No channels — add one in Settings" }));
      return;
    }
    setNotifying(domain.id);
    setNotifyMsg((m) => ({ ...m, [domain.id]: "" }));
    const clientName = clients.find((c) => c.id === domain.clientId)?.name ?? `Client ${domain.clientId}`;
    const { sent, failed } = await sendToChannels(domain, clientName);
    setNotifyMsg((m) => ({
      ...m,
      [domain.id]: failed === 0
        ? `Sent to ${sent} channel${sent === 1 ? "" : "s"}`
        : sent === 0
        ? `Failed (${failed} error${failed === 1 ? "" : "s"})`
        : `Sent ${sent}, failed ${failed}`,
    }));
    setNotifying(null);
  }

  const visibleDomains = domains.filter((d) => {
    if (filterClient !== "all" && String(d.clientId) !== filterClient) return false;
    if (filterRisk !== "all") {
      const risk = d.latestSnapshot?.risk ?? "unknown";
      if (risk !== filterRisk) return false;
    }
    if (filterType === "internal" && !d.isInternal) return false;
    if (filterType === "external" && d.isInternal) return false;
    return true;
  });

  if (loading) return <div className="text-gray-500 text-sm">Loading…</div>;
  if (error)   return <div className="text-red-400 text-sm">Error: {error}</div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">Domains</h1>
        <button
          onClick={() => { setShowForm((v) => !v); setFormError(null); }}
          className="text-sm px-3 py-1.5 rounded bg-gray-800 hover:bg-gray-700 text-white transition-colors"
        >
          {showForm ? "Cancel" : "+ Add Domain"}
        </button>
      </div>

      {/* Filter bar */}
      <div className="flex flex-wrap gap-2 items-center">
        <select
          value={filterClient}
          onChange={(e) => setFilterClient(e.target.value)}
          className="bg-gray-800 border border-gray-700 rounded px-2 py-1 text-xs text-gray-300 focus:outline-none focus:border-gray-500"
        >
          <option value="all">All clients</option>
          {clients.map((cl) => (
            <option key={cl.id} value={String(cl.id)}>{cl.name}</option>
          ))}
        </select>

        <select
          value={filterRisk}
          onChange={(e) => setFilterRisk(e.target.value)}
          className="bg-gray-800 border border-gray-700 rounded px-2 py-1 text-xs text-gray-300 focus:outline-none focus:border-gray-500"
        >
          <option value="all">All risk levels</option>
          <option value="green">Green</option>
          <option value="amber">Amber</option>
          <option value="red">Red</option>
          <option value="unknown">Unknown</option>
        </select>

        <select
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
          className="bg-gray-800 border border-gray-700 rounded px-2 py-1 text-xs text-gray-300 focus:outline-none focus:border-gray-500"
        >
          <option value="all">All types</option>
          <option value="external">External</option>
          <option value="internal">Internal</option>
        </select>

        {(filterClient !== "all" || filterRisk !== "all" || filterType !== "all") && (
          <button
            onClick={() => { setFilterClient("all"); setFilterRisk("all"); setFilterType("all"); }}
            className="text-xs text-gray-500 hover:text-gray-300 transition-colors"
          >
            Clear filters
          </button>
        )}

        <span className="text-xs text-gray-600 ml-auto">
          {visibleDomains.length} of {domains.length}
        </span>
      </div>

      {showForm && (
        <form
          onSubmit={handleSubmit}
          className="border border-gray-700 rounded-lg p-4 space-y-3 bg-gray-900 max-w-xl"
        >
          <div className="text-sm font-medium text-gray-300">Add Domain</div>

          <div className="space-y-1">
            <label className="text-xs text-gray-500">Client</label>
            <select
              required
              value={form.clientId}
              onChange={(e) => setForm((f) => ({ ...f, clientId: e.target.value }))}
              className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-1.5 text-sm text-white focus:outline-none focus:border-gray-500"
            >
              <option value="">Select a client…</option>
              {clients.map((cl) => (
                <option key={cl.id} value={cl.id}>{cl.name}</option>
              ))}
            </select>
            {clients.length === 0 && (
              <p className="text-xs text-amber-400">No clients yet — add a client first.</p>
            )}
          </div>

          <div className="space-y-1">
            <label className="text-xs text-gray-500">Hostname</label>
            <input
              required
              value={form.hostname}
              onChange={(e) => setForm((f) => ({ ...f, hostname: e.target.value }))}
              placeholder="example.com"
              className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-1.5 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-gray-500 font-mono"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <label className="text-xs text-gray-500">Port</label>
              <input
                type="number"
                min={1}
                max={65535}
                value={form.port}
                onChange={(e) => setForm((f) => ({ ...f, port: e.target.value }))}
                className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-1.5 text-sm text-white focus:outline-none focus:border-gray-500 font-mono"
              />
            </div>

            <div className="space-y-1">
              <label className="text-xs text-gray-500">Tags <span className="text-gray-600">(comma-separated)</span></label>
              <input
                value={form.tags}
                onChange={(e) => setForm((f) => ({ ...f, tags: e.target.value }))}
                placeholder="prod, web"
                className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-1.5 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-gray-500"
              />
            </div>
          </div>

          <label className="flex items-center gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={form.isInternal}
              onChange={(e) => setForm((f) => ({ ...f, isInternal: e.target.checked }))}
              className="w-4 h-4 accent-blue-500"
            />
            <span className="text-xs text-gray-400">
              Internal domain
              <span className="text-gray-600 ml-1">(private network — agent only, no direct scan)</span>
            </span>
          </label>

          {formError && <div className="text-red-400 text-xs">{formError}</div>}

          <button
            type="submit"
            disabled={submitting || clients.length === 0}
            className="px-4 py-1.5 rounded bg-blue-700 hover:bg-blue-600 disabled:opacity-50 text-sm text-white transition-colors"
          >
            {submitting ? "Adding…" : form.isInternal ? "Add Domain" : "Add & Scan"}
          </button>
        </form>
      )}

      <div className="overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="text-left text-gray-500 border-b border-gray-800">
              <th className="pb-2 pr-4">Hostname</th>
              <th className="pb-2 pr-4">Expires</th>
              <th className="pb-2 pr-4">Last Scanned</th>
              <th className="pb-2 pr-4">Source</th>
              <th className="pb-2 pr-4">Risk</th>
              <th className="pb-2 pr-4">Compliance</th>
              <th className="pb-2 pr-4">OCSP</th>
              <th className="pb-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {visibleDomains.map((d) => {
              const snap = d.latestSnapshot;
              return (
                <tr key={d.id} className="border-b border-gray-900 hover:bg-gray-900/40">
                  <td className="py-2 pr-4 font-mono">
                    {d.hostname}
                    {d.port !== 443 && <span className="text-gray-500">:{d.port}</span>}
                    {d.isInternal && (
                      <span className="ml-2 text-xs px-1.5 py-0.5 rounded bg-gray-800 text-gray-500 font-sans">internal</span>
                    )}
                    {d.tags.length > 0 && (
                      <span className="ml-2 text-gray-600 text-xs font-sans">{d.tags.join(", ")}</span>
                    )}
                  </td>
                  <td className="py-2 pr-4">
                    {snap ? (
                      <span>
                        {new Date(snap.notAfter).toLocaleDateString()}
                        <span className="text-gray-500 ml-1">({snap.daysRemaining}d)</span>
                      </span>
                    ) : (
                      <span className="text-gray-600">Not scanned</span>
                    )}
                  </td>
                  <td className="py-2 pr-4 text-xs text-gray-400">
                    {snap ? timeAgo(snap.scannedAt) : <span className="text-gray-600">—</span>}
                  </td>
                  <td className="py-2 pr-4">
                    {d.isInternal ? (
                      <span className="text-xs px-1.5 py-0.5 rounded bg-indigo-950 text-indigo-400">Agent</span>
                    ) : (
                      <span className="text-xs px-1.5 py-0.5 rounded bg-gray-800 text-gray-500">External</span>
                    )}
                  </td>
                  <td className="py-2 pr-4">
                    {snap ? (
                      <span className={`px-2 py-0.5 rounded text-xs ${riskBadge(snap.risk)}`}>
                        {snap.risk}
                      </span>
                    ) : <span className="text-gray-600 text-xs">—</span>}
                  </td>
                  <td className={`py-2 pr-4 text-xs ${snap ? complianceBadge(snap.complianceStatus) : "text-gray-600"}`}>
                    {snap?.complianceStatus ?? "—"}
                  </td>
                  <td className="py-2 pr-4 text-xs text-gray-400">
                    {snap?.ocspStatus ?? "—"}
                  </td>
                  <td className="py-2">
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => handleScan(d.id, d.hostname)}
                        disabled={scanning === d.id || deleting === d.id}
                        className="text-xs text-blue-400 hover:text-blue-300 disabled:opacity-40 transition-colors"
                        title={d.isInternal ? "Triggers scan via on-site agent" : undefined}
                      >
                        {scanning === d.id ? "Scanning…" : "Scan now"}
                      </button>
                      {scanMsg[d.id] && (
                        <span className="text-xs text-gray-500">{scanMsg[d.id]}</span>
                      )}
                      <button
                        onClick={() => handleNotify(d)}
                        disabled={notifying === d.id || deleting === d.id || scanning === d.id}
                        className="text-xs text-amber-500 hover:text-amber-400 disabled:opacity-40 transition-colors"
                        title="Send alert to configured notification channels"
                      >
                        {notifying === d.id ? "Sending…" : "Notify"}
                      </button>
                      {notifyMsg[d.id] && (
                        <span className="text-xs text-gray-500">{notifyMsg[d.id]}</span>
                      )}
                      <button
                        onClick={() => handleDelete(d.id, d.hostname)}
                        disabled={deleting === d.id || scanning === d.id}
                        className="text-xs text-red-500 hover:text-red-400 disabled:opacity-40 transition-colors"
                      >
                        {deleting === d.id ? "Removing…" : "Delete"}
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {visibleDomains.length === 0 && (
              <tr>
                <td colSpan={8} className="py-8 text-center text-gray-600">
                  {domains.length === 0 ? "No domains yet — add one above." : "No domains match the current filters."}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
